<template>
  <div class="page-wrap">

    <!-- QuickBooks sync overlay -->
    <div class="qb-overlay" v-if="isQuickBookSyncing">
      <div class="qb-overlay-inner">
        <i class="fa fa-spinner fa-spin"></i>
        Syncing with QuickBooks…
      </div>
    </div>

    <div class="panel-card">

      <!-- Header -->
      <div class="panel-header">
        <div class="panel-header-left">
          <div class="panel-icon"><i class="fa fa-file-invoice-dollar"></i></div>
          <div>
            <div class="panel-title">
              Client Invoice List
              <span class="count-badge" v-if="!load && !getLoadingData && reports.length > 0">
                {{ reports.length }} invoice{{ reports.length !== 1 ? 's' : '' }}
              </span>
            </div>
            <div class="panel-sub">Manage and review client invoices</div>
          </div>
        </div>
        <div class="panel-header-right">
          <router-link
            v-if="currentUser.u.is_admin == 1"
            :to="{ name: 'admin.client.invoice.create' }"
          >
            <button class="btn-create">
              <i class="fa fa-plus"></i> Create Invoice
            </button>
          </router-link>
          <router-link
            v-else
            :to="{ name: 'client.client.invoice.create', params: { client_slug: client_slug } }"
          >
            <button class="btn-create">
              <i class="fa fa-plus"></i> Create Invoice
            </button>
          </router-link>
        </div>
      </div>

      <!-- Filters -->
      <div class="filter-bar">
        <adv-search
          ref="advSearch"
          @search="advSearch"
          :is_date_range="true"
          :is_service="false"
          :is_status="false"
          :is_location_enrollment="false"
          :is_job_report="false"
          :is_admin_sp_location="true"
          :is_admin_sp="currentUser.u.is_admin == 1"
          :is_company="true"
          :is_sp_company="true"
          :is_payment_status="false"
          :reportData="excelreports"
          :titleworksheet="titleworksheet"
          :is_default_tramist_status="'200'"
          :json_fields="json_fields"
          :header="header"
          :report_name="'service_report.xls'"
        />
      </div>

      <!-- Initial prompt -->
      <div class="state-box" v-if="load">
        <i class="fa fa-filter"></i>
        Select the search criteria above and click Search
      </div>

      <!-- Loading -->
      <div class="state-box" v-else-if="getLoadingData">
        <i class="fa fa-spinner fa-spin"></i>
        Loading invoices…
      </div>

      <!-- Empty -->
      <div class="state-box" v-else-if="reports.length === 0">
        <i class="fa fa-file-invoice"></i>
        No invoices found for the selected criteria
      </div>

      <!-- Table -->
      <div class="table-wrap" v-else>
        <table class="inv-table">
          <thead>
            <tr>
              <th style="width:40px;">#</th>
              <th style="min-width:100px;">Invoice Date</th>
              <th style="min-width:110px;">Invoice No.</th>
              <th style="min-width:150px;">Invoice To</th>
              <th style="min-width:130px;">Invoice By</th>
              <th style="min-width:120px; text-align:center;">QuickBooks</th>
              <th style="width:60px;"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in reports" :key="index" class="inv-row">
              <td class="col-num">{{ index + 1 }}</td>
              <td class="col-date">{{ new Date(item.created_at).toLocaleDateString() }}</td>
              <td class="col-mono">{{ item.invoice_no }}</td>
              <td class="col-name">{{ item.invoice_to_name || item.sp_name }}</td>
              <td class="col-by">{{ item.created_by_name }}</td>
              <td class="col-qb">
                <div v-if="currentUser.u.is_admin">
                  <span class="qb-chip qb-synced" v-if="item.is_admin_quickbooks_synced">
                    <i class="fa fa-check"></i> Synced
                  </span>
                  <span class="qb-chip qb-pending" v-else>
                    <i class="fa fa-times"></i> Not Synced
                  </span>
                </div>
                <div v-else>
                  <span class="qb-chip qb-synced" v-if="item.is_quickbooks_synced">
                    <i class="fa fa-check"></i> Synced
                  </span>
                  <span class="qb-chip qb-pending" v-else>
                    <i class="fa fa-times"></i> Not Synced
                  </span>
                </div>
              </td>
              <td class="col-action">
                <div class="action-wrap" v-if="item.enrollment_id != ''">
                  <button
                    class="btn-action modal-box"
                    @click.stop="IndexClick(index)"
                  >
                    <i class="fa fa-ellipsis-v modal-box"></i>
                  </button>
                  <div class="action-dropdown" v-show="showActionButton === index">
                    <router-link
                      v-if="currentUser.u.is_admin == 1"
                      :to="{ name: 'admin.client.invoice.detail', params: { id: item.id } }"
                      class="action-item"
                    >
                      <i class="fa fa-eye"></i> View Detail
                    </router-link>
                    <router-link
                      v-else
                      :to="{ name: 'client.client.invoice.detail', params: { client_slug: client_slug, id: item.id } }"
                      class="action-item"
                    >
                      <i class="fa fa-eye"></i> View Detail
                    </router-link>
                    <div class="action-divider"></div>
                    <button
                      class="action-item action-delete"
                      @click="deleteInoiceMaster(item.invoice_master_id)"
                    >
                      <i class="fa fa-trash"></i> Delete
                    </button>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

    </div>
  </div>
</template>

<script>
import moment from "moment";
import {
  FETCH_CLIENTS,
  FETCH_SERVICES,
  FETCH_INVOICE_MASTER_LIST,
  DELETE_INVOICE_MASTER,
  FETCH_MASTER_COMPANIES,
  SYNC_QUICKBOOK_MASTER_INVOICE
} from "@/core/services/store/actions.type";
import {
  SET_BREADCRUMB,
  SET_COMPANY_INVOICE_APPOINTMENTS
} from "@/core/services/store/mutations.type";
import { mapGetters } from "vuex";
import AdvSearch from "@/includes/search/AdvSearch";

export default {
  components: { AdvSearch },
  computed: {
    ...mapGetters(["currentUser", "getClients", "getServices", "getMasterCompanies", "getSearchDate"])
  },
  data() {
    return {
      showActionButton: "",
      search: {
        service: "all",
        company: "all",
        date_from: "",
        date_to: "",
        service_status: "all",
        payment_status: "all"
      },
      titleworksheet: "",
      json_fields: {
        Company: "company_name",
        "Applicant Name": "applicant",
        Service: "service",
        Location: "client",
        "Service Status": "service_status",
        "Payment Status": "payment_status",
        "Scheduled Date": "schedule_date",
        Amount: "amount"
      },
      getLoadingData: false,
      breadCrumbs: [
        { id: 1, title: "Dashboard", route: "client.dashboard" },
        { id: 2, title: "Invoice List", route: "" }
      ],
      reports: [],
      header: [],
      load: true,
      excelreports: [],
      client_slug: "",
      isQuickBookSyncing: false
    };
  },
  mounted() {
    this.$store.dispatch(FETCH_CLIENTS);
    this.$store.commit(SET_COMPANY_INVOICE_APPOINTMENTS, []);
    this.$store.commit(SET_BREADCRUMB, this.breadCrumbs);
    this.$store.dispatch(FETCH_SERVICES, this.currentUser.u.is_client ? "self" : "all");
    this.$store.dispatch(FETCH_MASTER_COMPANIES);
    document.addEventListener("click", this.handleDocumentClick);
    this.client_slug = this.$route.params.client_slug;

    this.$nextTick(() => {
      const child = this.$refs.advSearch;
      if (!child) return;
      const today = this.$moment();
      child.search.date_from = today.startOf("month").format("YYYY-MM-DD");
      if (
        this.getSearchDate != {} &&
        this.getSearchDate != "" &&
        this.getSearchDate != null &&
        this.currentUser.u.is_admin == 1
      ) {
        this.advSearch({ header: {}, search: this.getSearchDate, footer: {} });
      } else {
        child.advSearch();
      }
    });
  },
  beforeDestroy() {
    document.removeEventListener("click", this.handleDocumentClick);
  },
  methods: {
    syncQuickBooks(id) {
      this.isQuickBookSyncing = true;
      this.$store.dispatch(SYNC_QUICKBOOK_MASTER_INVOICE, id)
        .then(() => {
          this.$toastr.s("Synced Successfully");
          document.getElementById("btn-search").click();
          this.isQuickBookSyncing = false;
        })
        .catch(e => {
          this.isQuickBookSyncing = false;
          this.$toastr.e(e.msg);
        });
    },
    deleteInoiceMaster(invoice_master_id) {
      this.$swal.fire({
        title: "Are you sure?",
        text: `You want to delete invoice ${invoice_master_id}`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes",
        cancelButtonText: "No"
      }).then(result => {
        if (result.isConfirmed) {
          this.$store.dispatch(DELETE_INVOICE_MASTER, invoice_master_id)
            .then(res => {
              this.$toastr.s(res.msg);
              this.$store.dispatch(FETCH_INVOICE_MASTER_LIST, this.search)
                .then(res => {
                  this.getLoadingData = false;
                  this.reports = res.data;
                })
                .catch(err => {
                  this.getLoadingData = false;
                  this.msg = err.error;
                });
            })
            .catch(errs => {
              this.$toastr.e(errs.error);
            });
        }
      });
    },
    handleDocumentClick(event) {
      if (!event.target.classList.contains("modal-box")) {
        this.IndexClick(-1);
      }
    },
    IndexClick(index) {
      this.showActionButton = this.showActionButton === index ? "" : index;
    },
    advSearch(value) {
      this.getLoadingData = true;
      this.load = false;
      const { search } = value;
      this.titleworksheet = search.date_from + " - " + search.date_to;
      this.search = { ...search };
      this.updateJsonFields();

      this.$store.dispatch(FETCH_INVOICE_MASTER_LIST, search)
        .then(res => {
          this.getLoadingData = false;
          this.reports = res.data;
        })
        .catch(err => {
          this.getLoadingData = false;
          this.msg = err.error;
        });
    },
    updateJsonFields() {
      if (this.search.company !== "all") {
        this.json_fields = {
          "Applicant Name": "applicant",
          Service: "service",
          Location: "client",
          "Service Status": "service_status",
          "Payment Status": "payment_status",
          "Scheduled Date": "schedule_date",
          Amount: "amount"
        };
      } else {
        this.json_fields = {
          Company: "company_name",
          "Applicant Name": "applicant",
          Service: "service",
          Location: "client",
          "Service Status": "service_status",
          "Payment Status": "payment_status",
          "Scheduled Date": "schedule_date",
          Amount: "amount"
        };
      }
    }
  }
};
</script>

<style lang="scss" scoped>
$font: 'Segoe UI', sans-serif;
$blue: #389ad4;
$dark: #0d1b3e;
$border: #dde2ec;

.page-wrap {
  padding: 8px 0 80px;
  font-family: $font;
  position: relative;
}

/* ── QuickBooks overlay ── */
.qb-overlay {
  position: absolute;
  inset: 0;
  background: rgba(13, 27, 62, 0.15);
  z-index: 10;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.qb-overlay-inner {
  background: #fff;
  border-radius: 10px;
  padding: 16px 28px;
  font-size: 13px;
  font-weight: 600;
  color: $dark;
  box-shadow: 0 4px 20px rgba(13, 27, 62, 0.15);
  display: flex;
  align-items: center;
  gap: 10px;

  i { color: $blue; font-size: 16px; }
}

/* ── Panel ── */
.panel-card {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(13, 27, 62, 0.07);
  overflow: visible;
}

.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  background: #fafbfd;
  border-bottom: 1px solid #eef0f6;
  gap: 12px;
  flex-wrap: wrap;
}

.panel-header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.panel-header-right {
  flex-shrink: 0;
}

.panel-icon {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: $blue;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  flex-shrink: 0;
}

.panel-title {
  font-size: 14px;
  font-weight: 700;
  color: $dark;
  display: flex;
  align-items: center;
  gap: 8px;
}

.count-badge {
  font-size: 11px;
  font-weight: 600;
  color: #7a8499;
  background: #eef0f6;
  border-radius: 10px;
  padding: 1px 7px;
}

.panel-sub {
  font-size: 11px;
  color: #8a94a6;
  margin-top: 2px;
}

.btn-create {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 34px;
  padding: 0 16px;
  border-radius: 7px;
  border: none;
  background: $blue;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  font-family: $font;
  cursor: pointer;
  text-decoration: none;
  transition: background 0.15s;

  i { font-size: 11px; }

  &:hover { background: #2d84b8; }
}

.filter-bar {
  padding: 12px 20px;
  border-bottom: 1px solid #f0f3fa;
}

/* ── States ── */
.state-box {
  text-align: center;
  padding: 52px 24px;
  color: #a0aab8;
  font-size: 13px;
  font-family: $font;

  i {
    font-size: 28px;
    display: block;
    margin-bottom: 10px;
    opacity: 0.4;
  }
}

/* ── Table ── */
.table-wrap { overflow-x: auto; }

.inv-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  font-family: $font;
  table-layout: auto;

  thead tr {
    border-bottom: 2px solid #eef0f8;

    th {
      padding: 9px 12px;
      font-size: 11px;
      font-weight: 700;
      color: #7a8499;
      text-transform: uppercase;
      letter-spacing: 0.4px;
      white-space: nowrap;
      text-align: left;
      background: #fff;
    }
  }

  tbody tr.inv-row {
    border-bottom: 1px solid #f5f7fa;
    transition: background 0.15s;

    &:last-child { border-bottom: none; }
    &:hover td { background: #f8fbff; }

    td {
      padding: 9px 12px;
      color: #2d3748;
      vertical-align: middle;
    }
  }
}

.col-num  { color: #a0aab8; font-size: 12px; width: 40px; }
.col-date { font-size: 12px; color: #6b7280; white-space: nowrap; }
.col-mono { font-family: 'Courier New', monospace; font-size: 12px; color: #374151; }
.col-name { font-weight: 500; color: $dark; max-width: 180px; word-break: break-word; }
.col-by   { font-size: 12.5px; color: #4a5568; }
.col-qb   { text-align: center; }

/* ── QuickBooks chips ── */
.qb-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 9px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;

  i { font-size: 10px; }
}

.qb-synced  { background: #d1fae5; color: #065f46; }
.qb-pending { background: #fee2e2; color: #991b1b; }

/* ── Action dropdown ── */
.col-action { width: 60px; text-align: center; }

.action-wrap {
  position: relative;
  display: inline-block;
}

.btn-action {
  width: 30px;
  height: 30px;
  border-radius: 6px;
  border: 1.5px solid $border;
  background: #fff;
  color: #7a8499;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;

  &:hover {
    background: #f0f3fa;
    border-color: #b0bac9;
    color: $dark;
  }
}

.action-dropdown {
  position: absolute;
  right: 0;
  top: calc(100% + 4px);
  background: #fff;
  border: 1.5px solid $border;
  border-radius: 8px;
  box-shadow: 0 6px 20px rgba(13, 27, 62, 0.12);
  z-index: 100;
  min-width: 140px;
  padding: 4px 0;
}

.action-item {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px 14px;
  font-size: 13px;
  color: #2d3748;
  font-family: $font;
  background: none;
  border: none;
  cursor: pointer;
  text-decoration: none;
  transition: background 0.12s;
  white-space: nowrap;

  i { font-size: 12px; width: 14px; text-align: center; opacity: 0.6; }

  &:hover {
    background: #f0f7fd;
    color: $blue;
    text-decoration: none;
  }
}

.action-delete {
  color: #dc2626;

  &:hover {
    background: #fff1f1;
    color: #dc2626;
  }
}

.action-divider {
  border-top: 1px solid #f0f3fa;
  margin: 3px 0;
}
</style>
